import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { staffApi, docsApi } from "../lib/api";
import { ArrowLeft, AlertTriangle, FileText, MessageCircle, Upload, Download, Eye, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CODLogoSVG } from "../components/cod/Logo";

export function CaseloadPage() {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  useEffect(()=>{ staffApi.caseload().then(setRows); }, []);
  const prColor = (p) => p === "P1" ? "destructive" : p === "P2" ? "default" : "outline";
  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <p className="cod-eyebrow">Staff CRM</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2">Caseload</h1>
          <div className="cod-rule-red mt-3"/>
        </div>
        <Card className="bg-white border-slate-200">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Participant</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Goal</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} data-testid={`caseload-row-${r.id}`}>
                    <TableCell className="font-medium whitespace-nowrap">{r.full_name}</TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{r.email}</TableCell>
                    <TableCell className="text-sm min-w-[200px]">{r.goal}</TableCell>
                    <TableCell>{r.has_plan ? <Badge variant="secondary">Yes</Badge> : <Badge variant="outline">No</Badge>}</TableCell>
                    <TableCell><Badge variant={prColor(r.priority)}>{r.priority}</Badge></TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={()=>navigate(`/staff/participants/${r.id}`)} data-testid={`view-${r.id}`}>View</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}

export function HandoffsPage() {
  const [packets, setPackets] = useState([]);
  const navigate = useNavigate();
  useEffect(()=>{ staffApi.packets().then(setPackets); }, []);
  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <p className="cod-eyebrow">Routine Transitions</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2">Handoff Packets</h1>
          <div className="cod-rule-red mt-3"/>
        </div>
        <p className="text-slate-600">Routine transitions from the Navigator. Each packet is a complete, ready-to-use briefing.</p>
        <div className="space-y-3">
          {packets.map(p => (
            <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`packet-${p.id}`}>
              <CardContent className="py-5">
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium">{p.participant?.full_name}</span>
                      <Badge variant={p.priority==="P1"?"destructive":p.priority==="P2"?"default":"outline"}>{p.priority}</Badge>
                      <Badge variant="secondary">{p.status}</Badge>
                    </div>
                    <div className="text-sm text-slate-700 mb-1"><strong>Goal:</strong> {p.goal}</div>
                    <div className="text-sm text-slate-600">{p.summary}</div>
                    <div className="text-xs text-slate-500 mt-2">Next step: {p.next_step}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={()=>navigate(`/staff/participants/${p.user_id}`)}>Open</Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {packets.length === 0 && <p className="text-sm text-slate-500">No handoff packets yet.</p>}
        </div>
      </div>
    </AppShell>
  );
}

export function StaffParticipantPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [visible, setVisible] = useState(false);
  const reload = () => staffApi.participant(id).then(setData);
  useEffect(()=>{ reload(); }, [id]);

  if (!data) return <AppShell><p>Loading...</p></AppShell>;
  const addNote = async () => {
    if (!note) return;
    await staffApi.note({ participant_id: id, body: note, visible_to_participant: visible });
    toast.success("Note recorded", { icon: <CODLogoSVG size={22}/> });
    setNote(""); reload();
  };
  return (
    <AppShell>
      <button onClick={()=>navigate(-1)} className="text-sm text-slate-600 flex items-center gap-1 mb-4" data-testid="back-btn"><ArrowLeft size={14}/> Back</button>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="font-serif text-2xl">{data.user.full_name}</CardTitle>
                  <p className="text-sm text-slate-500">{data.user.email}</p>
                </div>
                <Badge variant={data.profile?.last_priority==="P1"?"destructive":data.profile?.last_priority==="P2"?"default":"outline"}>
                  Priority: {data.profile?.last_priority || "P3"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {data.plan ? (
                <div className="space-y-2">
                  <div><span className="text-xs uppercase text-slate-500">Goal:</span> <span className="font-medium">{data.plan.data.goal}</span></div>
                  <div><span className="text-xs uppercase text-slate-500">Next step:</span> {data.plan.data.next_step_for_participant}</div>
                  <div className="flex flex-wrap gap-2 mt-2">{(data.plan.data.barriers||[]).map((b,i)=><Badge key={`${b}-${i}`} variant="secondary">{b}</Badge>)}</div>
                </div>
              ) : <p className="text-sm text-slate-500">No active plan.</p>}
            </CardContent>
          </Card>

          <Tabs defaultValue="conversations">
            <TabsList>
              <TabsTrigger value="conversations" data-testid="tab-conversations">Conversations</TabsTrigger>
              <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-docs">Documents</TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
            </TabsList>
            <TabsContent value="conversations" className="space-y-2 mt-4">
              {data.conversations.map(c => (
                <Card key={c.id}>
                  <CardContent className="py-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span>{new Date(c.updated_at).toLocaleString()}</span>
                      <Badge variant="outline">{c.priority}</Badge>
                    </div>
                    <div className="text-xs text-slate-500">{c.messages?.length || 0} messages</div>
                    <details className="text-sm mt-2">
                      <summary className="cursor-pointer text-slate-600">View transcript</summary>
                      <div className="mt-2 space-y-1 text-xs">
                        {(c.messages||[]).map((m,i)=>(
                          <div key={m.ts || `cmsg-${i}`}><strong>{m.role}:</strong> {m.text}</div>
                        ))}
                      </div>
                    </details>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
            <TabsContent value="tasks" className="space-y-2 mt-4">
              {data.tasks.map(t => (
                <div key={t.id} className="text-sm border-b py-2"><strong>{t.title}</strong> · <Badge variant="outline">{t.status}</Badge></div>
              ))}
            </TabsContent>
            <TabsContent value="documents" className="space-y-3 mt-4">
              <StaffDocsPanel participantId={id} docs={data.documents} onReload={reload}/>
            </TabsContent>
            <TabsContent value="notes" className="space-y-2 mt-4">
              {data.notes.map(n => (
                <Card key={n.id}><CardContent className="py-3 text-sm">
                  <div className="text-xs text-slate-500 mb-1">{new Date(n.created_at).toLocaleString()} · {n.visible_to_participant ? "Visible to participant" : "Internal"}</div>
                  {n.body}
                </CardContent></Card>
              ))}
            </TabsContent>
          </Tabs>
        </div>

        <div>
          <Card>
            <CardHeader><CardTitle className="font-serif text-lg">Record outcome / note</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Textarea placeholder="What happened in your contact? The Navigator will reference this next session..."
                value={note} onChange={e=>setNote(e.target.value)} rows={5} data-testid="staff-note-input"/>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={visible} onChange={e=>setVisible(e.target.checked)} data-testid="note-visible-check"/>
                Visible to participant
              </label>
              <Button onClick={addNote} className="w-full cod-bg-red" data-testid="save-note">Save note</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

// ---------- Staff Document Management ----------
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function StaffDocsPanel({ participantId, docs: initial, onReload }) {
  const [docs, setDocs] = useState(initial || []);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replaceFor, setReplaceFor] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const reload = () => {
    docsApi.participantDocs(participantId).then(setDocs).catch(()=>{});
    if (onReload) onReload();
  };
  useEffect(()=>{
    docsApi.categories().then(d => setCats(d.categories || [])).catch(()=>{});
    docsApi.participantDocs(participantId).then(setDocs).catch(()=>{});
  }, [participantId]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    setUploading(true);
    try {
      await docsApi.uploadForParticipant(participantId, form);
      toast.success("Document uploaded");
      setOpen(false); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const handleReplace = async (e) => {
    e.preventDefault();
    if (!replaceFor) return;
    const form = new FormData(e.target);
    setUploading(true);
    try {
      await docsApi.replace(replaceFor.id, form);
      toast.success("New version uploaded");
      setReplaceFor(null); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Replace failed"); }
    finally { setUploading(false); }
  };
  const handleDelete = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try { await docsApi.remove(d.id); toast.success("Deleted"); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };
  const handleVerify = async (d) => {
    try { await docsApi.verify(d.id, !d.verified); toast.success(d.verified ? "Marked unverified" : "Marked verified"); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
  };
  const openPreview = async (d) => {
    setPreviewDoc(d); setPreviewUrl("");
    try {
      const blob = await docsApi.fetchBlob(d.id, true);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch { toast.error("Preview failed"); }
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null); setPreviewUrl("");
  };
  const catLabel = (k) => (cats.find(c => c.key === k)?.label) || k;
  const visible = docs.filter(d => !d.is_deleted && d.status !== "superseded");

  return (
    <div className="space-y-3" data-testid="staff-docs-panel">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{visible.length} document{visible.length !== 1 ? "s" : ""} on file.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" style={{background:"var(--cod-navy)"}} data-testid="staff-upload-btn">
              <Upload size={14} className="mr-2"/> Upload for participant
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload document for participant</DialogTitle>
              <DialogDescription>Document will be saved to this participant&apos;s vault.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div><Label htmlFor="sd-name">Name</Label><Input id="sd-name" name="name" required data-testid="staff-upload-name"/></div>
              <div>
                <Label htmlFor="sd-cat">Category</Label>
                <select id="sd-cat" name="category" required className="w-full border rounded-md p-2 bg-white" data-testid="staff-upload-category">
                  {cats.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="sd-vis" name="visible_to_participant" value="true" defaultChecked/>
                <Label htmlFor="sd-vis" className="text-sm">Visible to participant</Label>
              </div>
              <div><Label htmlFor="sd-file">File</Label><Input id="sd-file" type="file" name="file" required data-testid="staff-upload-file"/></div>
              <Button type="submit" disabled={uploading} style={{background:"var(--cod-navy)"}} data-testid="staff-upload-submit">
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {visible.length === 0 && <p className="text-sm text-slate-500">No documents on file.</p>}
      {visible.map(d => {
        const previewable = d.content_type?.startsWith("image/") || d.content_type === "application/pdf";
        return (
          <Card key={d.id} data-testid={`staff-doc-${d.id}`}>
            <CardContent className="py-3">
              <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <FileText size={18} className="text-slate-400 mt-0.5"/>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                      {d.name}
                      {d.verified && <Badge style={{background:"var(--cod-navy-soft)", color:"var(--cod-navy)"}}><ShieldCheck size={12} className="mr-1"/>Verified</Badge>}
                      {d.version > 1 && <Badge variant="outline">v{d.version}</Badge>}
                      {!d.visible_to_participant && <Badge variant="outline" className="text-amber-700 border-amber-300">Internal</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {catLabel(d.category)} · {d.filename} · {new Date(d.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {previewable && <Button size="sm" variant="outline" onClick={()=>openPreview(d)}><Eye size={14} className="mr-1"/>Preview</Button>}
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`}
                     className="inline-flex items-center px-3 py-1.5 text-sm font-medium border rounded-md hover:bg-slate-50"
                     style={{color:"var(--cod-navy)", borderColor:"var(--cod-navy)"}}>
                    <Download size={14} className="mr-1"/>Download
                  </a>
                  <Button size="sm" variant="outline" onClick={()=>handleVerify(d)}>
                    <ShieldCheck size={14} className="mr-1"/>{d.verified ? "Unverify" : "Verify"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={()=>setReplaceFor(d)}><RefreshCw size={14} className="mr-1"/>Replace</Button>
                  <Button size="sm" variant="outline" onClick={()=>handleDelete(d)} className="text-red-600 hover:bg-red-50 border-red-200"><Trash2 size={14} className="mr-1"/>Delete</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={!!replaceFor} onOpenChange={(o)=>{ if(!o) setReplaceFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload new version</DialogTitle>
            <DialogDescription>Replacing <strong>{replaceFor?.name}</strong>.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReplace} className="space-y-4">
            <div><Label htmlFor="srep-file">New file</Label><Input id="srep-file" type="file" name="file" required/></div>
            <Button type="submit" disabled={uploading} style={{background:"var(--cod-navy)"}}>{uploading ? "Uploading..." : "Upload new version"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewDoc} onOpenChange={(o)=>{ if(!o) closePreview(); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewDoc?.name}</DialogTitle>
            <DialogDescription>{previewDoc?.filename}</DialogDescription>
          </DialogHeader>
          <div className="h-[70vh] bg-slate-50 border rounded-md overflow-hidden flex items-center justify-center">
            {!previewUrl ? <p className="text-slate-500">Loading preview…</p>
              : previewDoc?.content_type?.startsWith("image/")
                ? <img src={previewUrl} alt={previewDoc?.name} className="max-w-full max-h-full object-contain"/>
                : <iframe src={previewUrl} title={previewDoc?.name} className="w-full h-full"/>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
